import { useSchedulerV3InvalidateQueries } from '../helper-hooks';
import { AppointmentTypeMutationContext, useSchedulerV3QueryUpdaters } from '../query-updaters';
import { UpdateAppointmentTypeIO } from '../types';
import { UseSchedulerV3MutationEndpointArgs, useSchedulerV3Mutation } from './use-scheduler-v3-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  mutationContext?: AppointmentTypeMutationContext;
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `UpdateAppointmentType` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useUpdateAppointmentTypeMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends UpdateAppointmentTypeIO['input'] = UpdateAppointmentTypeIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseSchedulerV3MutationEndpointArgs<
  'UpdateAppointmentType',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const {
    invalidateListAppointmentTypes,
    invalidateGetAppointmentType,
    invalidateListCalendarEvents,
    invalidateListCalendarEventsByAttendee,
  } = useSchedulerV3InvalidateQueries();
  const { updateAppointmentType } = useSchedulerV3QueryUpdaters();

  return useSchedulerV3Mutation<'UpdateAppointmentType', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'UpdateAppointmentType',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here
        const { _httpOptions, _otherOptions, appointmentTypeId, ...appointmentTypeInfo } = request;

        return {
          // Add custom context type here (if exists in `MutationContext`)
          mutationContext: optimisticUpdate ? updateAppointmentType(appointmentTypeId, appointmentTypeInfo) : undefined,
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        // Implement query updates or invalidation for affected queries here
        const locationId = response.appointmentType.locationId;
        invalidateListAppointmentTypes(locationId);
        invalidateGetAppointmentType(request.appointmentTypeId);
        invalidateListCalendarEvents({ locationId, ignoreDate: true });
        invalidateListCalendarEventsByAttendee(locationId);

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)
        if (context?.mutationContext?.originalData) {
          updateAppointmentType(request.appointmentTypeId, context.mutationContext.originalData);
        }

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
